import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import StepRow from '../components/StepRow';
import PartnerEntry from '../components/PartnerEntry';

export default function Financing() {
  return (
    <>
      <PageHero
        eyebrow="Financing path"
        title="Working capital that knows the"
        emphasis="guard business."
        lede="Payroll runs every two weeks but customers pay in 45 to 90 days. The gap kills good companies. OfficerCapital connects you to lenders who finance only security guard companies — so they understand the receivables, the contracts, and the cycle."
      >
        <a href="https://officercapital.com/register" className="oc-btn oc-btn--primary">List your company</a>
        <Link to="/how-it-works" className="oc-btn oc-btn--link">See how it works →</Link>
      </PageHero>

      {/* WHAT IT'S FOR */}
      <section className="oc-section">
        <div className="oc-shell">
          <div className="oc-grid-1-2 oc-mb-5">
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">What financing covers</div>
              <h2 className="oc-h2">
                The cash gap, <em className="oc-em">closed</em>.
              </h2>
            </div>
            <p className="oc-lede oc-max-520" style={{ paddingTop: 14, fontSize: 16 }}>
              The lenders on OfficerCapital specialize in guard receivables. They underwrite based on contract quality
              and collection history, not personal guarantees on your house.
            </p>
          </div>

          <div className="oc-grid-3">
            <UseCaseCard
              label="Payroll factoring"
              body="Advance against unpaid invoices the day after payroll runs. Stop chasing customers for 60 days while your officers wait."
            />
            <UseCaseCard
              label="Working capital lines"
              body="Revolving credit sized to your AR and contract pipeline. Draw what you need, when you need it."
            />
            <UseCaseCard
              label="Equipment & vehicle finance"
              body="Patrol vehicles, body cameras, dispatch systems. Asset-backed financing without dipping into operating cash."
            />
          </div>
        </div>
      </section>

      {/* WHO'S ON THE OTHER SIDE */}
      <section className="oc-section oc-section--white">
        <div className="oc-shell">
          <div className="oc-grid-2" style={{ alignItems: 'start' }}>
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">Who you'll talk to</div>
              <h2 className="oc-h2">
                Lenders who've underwritten <em className="oc-em">guard companies</em> before yours.
              </h2>
              <p className="oc-body oc-mt-3 oc-max-440">
                Generalist factoring desks treat security like any other service business — and price the unfamiliarity into the rate.
                The lenders here know the difference between a state contract and a strip-mall account, and price accordingly.
              </p>
            </div>
            <div className="oc-border-l">
              <div className="oc-stack">
                <ListPoint title="Pre-vetted by OfficerCapital" body="Every lender is manually approved before they see your listing." />
                <hr className="oc-rule" />
                <ListPoint title="Industry-specialized" body="They finance security companies as a primary book of business, not a side bet." />
                <hr className="oc-rule" />
                <ListPoint title="Pay to engage" body="Lenders pay a subscription and a $500 unlock fee per deal — so they only reach out if they're serious." />
                <hr className="oc-rule" />
                <ListPoint title="Compete for your business" body="Multiple lenders can request access to the same listing. You decide who gets a conversation." />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — financing flavor */}
      <section className="oc-section">
        <div className="oc-shell">
          <div className="oc-grid-1-2 oc-mb-5">
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">How financing works on OfficerCapital</div>
              <h2 className="oc-h2">
                From listing to first term sheet, <em className="oc-em">in days</em>.
              </h2>
            </div>
            <p className="oc-lede oc-max-520" style={{ paddingTop: 14, fontSize: 16 }}>
              Most owners hear back from at least one lender within 72 hours. You set the timeline from there.
            </p>
          </div>

          <div className="oc-grid-3-wide">
            <StepRow number="01" title="List anonymously">
              Headline metrics, AR aging, contract mix, current banking. We never ask for your company name and never publish it.
            </StepRow>
            <StepRow number="02" title="Lenders request access">
              Vetted lenders see your listing and pay to unlock the full file. You see who's interested before they see you.
            </StepRow>
            <StepRow number="03" title="Approve, talk, decide">
              Approve the lenders worth your time. Compare term sheets in one place. Close on whatever terms you negotiate — we don't take a cut.
            </StepRow>
          </div>
        </div>
      </section>

      {/* OTHER PATHS */}
      <section className="oc-section oc-section--white">
        <div className="oc-shell">
          <div className="oc-eyebrow-gray oc-mb-3">Other paths</div>
          <div className="oc-grid-2">
            <RelatedPathCard
              to="/investors"
              eyebrow="Path 02"
              title="You want an investor instead"
              body="Looking for growth capital or a minority partner? See how investors connect on OfficerCapital."
            />
            <RelatedPathCard
              to="/sell"
              eyebrow="Path 03"
              title="You're thinking about selling"
              body="Considering a full sale? See how owners connect with vetted acquirers."
            />
          </div>
        </div>
      </section>

      <PartnerEntry />
    </>
  );
}

function UseCaseCard({ label, body }) {
  return (
    <div className="oc-card">
      <div className="oc-eyebrow-gray oc-mb-2">{label}</div>
      <p className="oc-body-sm" style={{ fontSize: 13, lineHeight: 1.55 }}>{body}</p>
    </div>
  );
}

function ListPoint({ title, body }) {
  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--oc-ink)', marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 13, color: 'var(--oc-gray)', lineHeight: 1.55 }}>{body}</div>
    </div>
  );
}

function RelatedPathCard({ to, eyebrow, title, body }) {
  return (
    <Link to={to} className="oc-card oc-card--linked" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
      <div className="oc-eyebrow oc-mb-2">{eyebrow}</div>
      <h3 className="oc-h3" style={{ marginBottom: 8 }}>{title}</h3>
      <p className="oc-body-sm" style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 12 }}>{body}</p>
      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--oc-ink)' }}>Read more →</span>
    </Link>
  );
}
